// API client — communicates with Cloudflare Workers API
// Mirrors mobile app's api.client.ts pattern
// Auto-attaches Supabase JWT token to every request
import { env } from '@config/env.js';
import { API_TIMEOUT } from '@config/constants.js';
import { ApiError } from './errors.js';
import { supabase } from '@services/supabase/client.js';

/**
 * Get current auth token from Supabase session.
 * Token is auto-refreshed by Supabase client if expiring.
 * @returns {Promise<string|null>}
 */
async function getAuthToken() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.warn('[ApiClient] getSession error:', error.message);
      return null;
    }

    if (session?.access_token) {
      // Check if token is expiring soon (within 60 seconds)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);

      if (expiresAt && expiresAt - now < 60) {
        // Token expiring soon, try to refresh
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError || !refreshData?.session) {
          console.warn('[ApiClient] Token refresh failed:', refreshError?.message);
          // Still return old token, it might still work
          return session.access_token;
        }

        return refreshData.session.access_token;
      }

      return session.access_token;
    }

    // No session found — try refreshing (might restore from storage)
    const { data: refreshData } = await supabase.auth.refreshSession();
    return refreshData?.session?.access_token ?? null;
  } catch (err) {
    console.error('[ApiClient] Error getting auth token:', err);
    return null;
  }
}

/**
 * API Client for Cloudflare Workers backend.
 * Auto-attaches Supabase JWT, handles errors, timeout.
 */
class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl || `${env.apiBaseUrl}/${env.apiVersion}`;
  }

  /**
   * Make an HTTP request to the Worker API
   * @param {string} endpoint - API endpoint (e.g. '/jobs')
   * @param {Object} options - { method, body, headers, skipAuth, timeout }
   * @returns {Promise<*>}
   */
  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Auto-attach auth token unless explicitly skipped
    if (!options.skipAuth) {
      const token = await getAuthToken();
      if (!token) {
        console.error('[ApiClient] No auth token available for:', endpoint);
        throw new ApiError(401, 'Not authenticated - please login again', { code: 'NO_TOKEN' });
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.timeout || API_TIMEOUT);

    try {
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers,
        body: options.body,
        signal: controller.signal,
      });

      // Read body as text first (can only be read once)
      const responseText = await response.text();

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { error: responseText || 'Unknown error' };
        }
        console.error('[ApiClient] Error response:', response.status, errorData);
        throw new ApiError(
          response.status,
          errorData.error || response.statusText,
          { code: errorData.code, details: errorData.details }
        );
      }

      // Parse successful response
      try {
        return JSON.parse(responseText);
      } catch {
        return {};
      }
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error.name === 'AbortError') {
        throw new ApiError(408, 'Request timeout');
      }
      throw new ApiError(0, error.message || 'Network error');
    } finally {
      clearTimeout(timeout);
    }
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  patch(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  del(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

// Singleton instance
export const apiClient = new ApiClient();
